import { BidsAsks } from '../types/orderbook';
import { Gwei } from './gwei';
import { multiply } from './math';
import { calcMaxSizeWithLimitPrice } from './maxPositionSize.limit';
import { calcMaxSizeForMarketOrder } from './maxPositionSize.market';

/**
 * Calculates the total margin rate required for position sizing
 * This includes initial margin plus round-trip trading fees
 */
export function calcMarginRate(initialMarginRatioBigInt: bigint, feeRate: bigint): bigint {
  // Include initial margin plus round-trip fee (entry + exit)
  return initialMarginRatioBigInt + feeRate + feeRate;
}

export interface CalcTotalCostPerUnitParams {
  markPrice: bigint; // The base price used for margin calculation
  marginRate: bigint; // Already computed via calcMarginRate
  priceDifference?: bigint; // e.g., immediate slippage or unfavorable fill
}

/**
 * Calculates the total cost per unit of position,
 * combining margin + fees + optional price difference (slippage).
 */
export function calcTotalCostPerUnit({
  markPrice,
  marginRate,
  priceDifference = 0n,
}: CalcTotalCostPerUnitParams): bigint {
  return multiply(markPrice, marginRate) + priceDifference;
}
/**
 * Calculates the notional value of a position that can be used to offset margin requirements
 * when closing an existing position
 */
function calcPositionClosingOffset(
  currentPositionSize: bigint,
  maxSize: bigint,
  side: 'long' | 'short'
): bigint {
  const isClosingPositionDirection =
    (side === 'long' && currentPositionSize < 0n) || (side === 'short' && currentPositionSize > 0n);

  if (isClosingPositionDirection) {
    // Offset margin requirements for closing position
    // If the position is larger than the current max size we use the max size as offset.
    return Gwei.min(maxSize, Gwei.abs(currentPositionSize));
  }

  return 0n;
}

export type CalcMaxPositionParams = {
  availableMargin: bigint | undefined;
  oraclePrice: bigint | undefined;
  feeRate: bigint;
  initialMarginRatio: number;
  maxQuantity: bigint;
  limitPrice?: bigint;
  currentPositionSize: bigint;
  reduceOnly: boolean;
  side: 'long' | 'short';
  bidsAsks: BidsAsks | undefined;
  lotSize: bigint;
};

/**
 * Calculates the maximum size that can be traded given available margin,
 * fees, and other constraints
 */
export function calcMaxSize(params: CalcMaxPositionParams) {
  const {
    limitPrice,
    bidsAsks,
    oraclePrice,
    availableMargin,
    reduceOnly,
    currentPositionSize,
    side,
  } = params;
  if (!availableMargin || !oraclePrice) {
    return undefined;
  }

  // Reduce-only orders can be calculated directly
  if (reduceOnly) {
    return side === 'long'
      ? currentPositionSize >= 0
        ? 0n
        : Gwei.abs(currentPositionSize)
      : currentPositionSize <= 0
        ? 0n
        : Gwei.abs(currentPositionSize);
  }

  // Calculate max size using the appropriate method
  let maxSize;

  if (bidsAsks && !limitPrice) {
    // For market orders with available orderbook data
    maxSize = calcMaxSizeForMarketOrder({
      ...params,
      bidsAsks,
    });
  } else if (limitPrice) {
    // For limit orders
    maxSize = calcMaxSizeWithLimitPrice(params);
  }

  if (maxSize === undefined) {
    return undefined;
  }
  // Apply position closing offset if applicable
  const closingOffset = calcPositionClosingOffset(currentPositionSize, maxSize, side);
  return Gwei.min(maxSize + closingOffset, params.maxQuantity);
}
